import { PageLayout } from '@/shared/components/PageLayout';
import { ShopifyList } from '@/shared/components/ShopifyList';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import type { ShopifyListColumn } from '@/shared/types/shopify';
import { CBadge, CButton, CTooltip } from '@coreui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Customer, CustomerFilters } from '../../types';
import { getSubscriptionBadgeColor } from '../../utils/badgeColors';
import {
  getSubscriptionStatusDisplayValue,
  getSubscriptionStatusTooltip,
} from '../../utils/subscriptionUtils';

const CustomersList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const columns: ShopifyListColumn<Customer>[] = [
    {
      header: 'Shopify ID',
      key: 'shopifyCustomerId',
    },
    {
      header: 'Name',
      key: 'fullName',
    },
    {
      header: 'Email',
      key: 'email',
    },
    {
      header: 'Subscription Plan Status',
      key: 'subscriptionStatus',
      render: (customer: Customer) => (
        <CTooltip content={getSubscriptionStatusTooltip(customer.subscriptionStatus)}>
          <CBadge color={getSubscriptionBadgeColor(customer.subscriptionStatus)}>
            {getSubscriptionStatusDisplayValue(customer.subscriptionStatus)}
          </CBadge>
        </CTooltip>
      ),
    },
    {
      header: 'Actions',
      key: 'shopifyCustomerId',
      render: (customer: Customer) =>
        hasPermission(USER_PERMISSIONS.CustomersView) && (
          <CButton
            color="primary"
            size="sm"
            onClick={() => void navigate(`/customers/${customer.shopifyCustomerId}`)}
          >
            View
          </CButton>
        ),
    },
  ];

  return (
    <PageLayout customBreadcrumbLabels={{ dashboard: '' }}>
      <ShopifyList<Customer, CustomerFilters>
        title="Customers"
        endpoint="/admin/customers"
        columns={columns}
        defaultFilters={{ search: '', referrerCode: '' }}
        filterConfigs={[
          {
            type: 'search',
            key: 'search',
            placeholder: 'Search by Shopify ID, Name, or e-mail (partial string search supported)',
          },
          { type: 'search', key: 'referrerCode', placeholder: 'Search by referrer code' },
        ]}
        getRowKey={(customer) => customer.shopifyCustomerId}
        pageSizeOptions={[10, 25, 50, 100]}
        requiredViewPermission={USER_PERMISSIONS.CustomersView}
      />
    </PageLayout>
  );
};

export { CustomersList };
